<template>
  <div>
    <div
      v-if="sectionInfo && isVisible"
      v-on-display="{ sectionInfo, context: this }"
      class="multi autoplay-band"
    >
      <div class="title">
        <h1 v-band-title class="title-area" tabindex="0">
          <span class="label">{{ multiSectionTitle }}</span>
        </h1>
      </div>
      <lazy-component @show="setData()">
        <swiper :options="swiperOptions">
          <swiper-slide
            v-for="(cell, index) in cellList"
            :key="`${index}__${cell?.alt ?? ''}`"
            ref="cellListSwiper"
            class="swiper swiper-container-horizontal"
          >
            <PreviewInstantPlayerCell
              v-if="isAutoPlayerCell(cell)"
              :cell-info="cell"
              :thumbnail-info="thumbnailInfo"
              :is-player-mute="isPlayerMute"
              :set-player-mute-state-fn="setPlayerMuteState"
            />
            <PreviewInstantImageCell
              v-else
              :cell-info="cell"
              :thumbnail-info="thumbnailInfo"
            />
          </swiper-slide>
          <div slot="button-prev" class="swiper-button-prev"></div>
          <div slot="button-next" class="swiper-button-next"></div>
        </swiper>
      </lazy-component>
      <template>
        <a
          v-if="hasViewMore"
          href="#"
          class="btn-more all"
          @click.prevent="viewMore(sectionInfo)"
          >{{ viewMoreTitle(sectionInfo, '더보기') }}</a
        >
      </template>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import { swiper, swiperSlide } from 'vue-awesome-swiper'
  import MultiSectionMixin from '@/mixins/multisection.mixin'
  import PreviewInstantPlayerCell from '@/components/model/cell/PreviewInstantPlayerCell'
  import { bandTitle, onDisplay } from '@/common/resources/directives'
  import {
    EVENT_TYPE_MOUSE_ENTER,
    EVENT_TYPE_MOUSE_LEAVE,
  } from '@/common/resources/const'
  import canAutoPlay from 'can-autoplay'
  import PreviewInstantImageCell from '@/components/model/cell/PreviewImageCell'
  import _ from 'lodash'
  import { convertBand } from '@/common/converter/convert-band'
  import { previewCell } from '@/common/utils/like-status/preview-cell'
  import {
    COMMON_SWIPER_OPTIONS,
    SLIDES_PER_GROUP,
  } from '@/common/resources/swiper.const'

  export default {
    name: 'PreviewCatalog',
    components: {
      swiper,
      swiperSlide,
      PreviewInstantPlayerCell,
      PreviewInstantImageCell,
    },
    directives: {
      bandTitle,
      onDisplay,
    },
    mixins: [MultiSectionMixin],
    props: {
      catalogInfo: {
        type: Object,
        default: () => {},
      },
      sectionInfo: {
        type: Object,
        default: () => {},
      },
      thumbnailInfo: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        isVisible: true,
        bandInfo: null,
        cellList: [],
        isLoading: false,
        isPlayerMute: false,
        firedEventMap: {},
        swiperOptions: {
          height: this.thumbnailInfo.height,
          spaceBetween: COMMON_SWIPER_OPTIONS.SPACE_BETWEEN,
          slidesPerView: COMMON_SWIPER_OPTIONS.SLIDES_PER_VIEW,
          slidesPerGroup: SLIDES_PER_GROUP.HORIZONTAL_L,
          nextButton: '.swiper-button-next',
          prevButton: '.swiper-button-prev',
          observer: true,
        },
      }
    },
    created() {
      this.initialize()
      Vue.prototype.$previewEvents = new Vue()
      this.$previewEvents.$on(EVENT_TYPE_MOUSE_ENTER, this.startPlayerInit)
      this.$previewEvents.$on(EVENT_TYPE_MOUSE_LEAVE, this.disposePlayer)
    },
    beforeDestroy() {
      this.$previewEvents.$off(EVENT_TYPE_MOUSE_ENTER, this.startPlayerInit)
      this.$previewEvents.$off(EVENT_TYPE_MOUSE_LEAVE, this.disposePlayer)
    },
    methods: {
      async setData() {
        if (this.cellList.length !== 0) return
        this.bandInfo = await convertBand(this.catalogInfo)
        this.cellList = previewCell(this.bandInfo?.band.celllist ?? [])

        if (this.cellList.length === 0) this.isVisible = false
      },
      clearAlreadyFiredEvent(contentId) {
        const keyList = Object.keys(this.firedEventMap)
        keyList.forEach((key) => {
          if (contentId !== key) {
            clearTimeout(this.firedEventMap[key].timer)
            setTimeout(() => {
              if (this.firedEventMap[key]) {
                this.firedEventMap[key].context.disableVideoMode()
                this.firedEventMap[key].context = null
                delete this.firedEventMap[key]
              }
            }, 0)
          }
        })
      },
      fireVideoInitEvent(contentId, context) {
        this.firedEventMap[contentId] = {
          timer: setTimeout(() => {
            canAutoPlay
              .video()
              .then(({ result }) => {
                if (!result) {
                  sessionStorage.setItem(this.MUTE_STATE_KEY, 'true')
                  this.isPlayerMute = true
                }
                context.enableVideoMode()
              })
              .catch(() => {
                context.enableVideoMode()
              })
          }, 1000),
          context: context,
        }
      },
      startPlayerInit(context) {
        const { contentId } = context.getInstancePlayMeta()
        this.clearAlreadyFiredEvent(contentId, context)
        this.fireVideoInitEvent(contentId, context)
      },
      disposePlayer() {
        this.clearAlreadyFiredEvent('EMPTY')
      },
      setPlayerMuteState(state) {
        sessionStorage.setItem(this.MUTE_STATE_KEY, String(state))
        this.isPlayerMute =
          sessionStorage.getItem(this.MUTE_STATE_KEY) === 'true'
      },
      getMuteState() {
        const isMute = sessionStorage.getItem(this.MUTE_STATE_KEY)
        if (isMute === null) {
          sessionStorage.setItem(this.MUTE_STATE_KEY, 'true')
        }
        return sessionStorage.getItem(this.MUTE_STATE_KEY) === 'true'
      },
      initialize() {
        this.isPlayerMute = this.getMuteState()
        this.setData()
      },
      isAutoPlayerCell(cell) {
        return _.get(cell, 'event_list', []).length > 0
      },
    },
  }
</script>
<style lang="scss" src="@/assets/scss/category/new_band.scss"></style>
